import { createSlice } from '@reduxjs/toolkit';

import { api } from './api';

type Project = 'growth' | 'seller' | 'exclusives';
type ExpYear = '23' | '24' | '25';
type ExpMonth = '01' | '02' | '03' | '04' | '05' | '06' | '07' | '08' | '09' | '10' | '11' | '12';
type ExperimentName = `${Project}-${ExpYear}-${ExpMonth}-${string}`;

/**
 * Replacement for TS satisfies operator, until it's well supported
 * Usage:
 * const test = satisfy<{ a: string }>()({ a: 'test', b: 'test' })
 * Why this is needed even though we use typescript 4.9.5
 * - because babel barfs on `satisfied` atm
 */
function satisfy<TSatisfied>(): <T extends TSatisfied>(value: T) => T {
  return (value) => value;
}

const experimentFlagMap = satisfy<Record<string, ExperimentName>>()({
  /**
   * legacy experiment names - do not copy `as ExperimentName` and instead follow
   * the ExperimentName type for your new experiments
   */

  // use `growth` instead `pg` - do not add more of these
  longboarding: 'pg-24-04-longboarding-no-stainless' as ExperimentName,
  shortboarding: 'pg-24-03-shortboarding' as ExperimentName,
  /* end legacy flags */

  lowConfidenceBasicData: 'growth-24-05-low-confidence-basic-data',
  isPreviouslyListedExperimentFlag: 'growth-24-06-hdq-not-previously-listed-home',
  addressEntryConfirmation: 'growth-24-07-address-entry-confirmation',
} as const);

export type ExperimentFlag = keyof typeof experimentFlagMap;

//TODO: need to have an "unknown" value here, since null means "not in experiment"
export type ExperimentVariant = string | null;
export type ExperimentFlags = Record<ExperimentFlag, ExperimentVariant>;

export const getExperimentKey = (flag: ExperimentFlag) => {
  return experimentFlagMap[flag];
};

// Reverse lookup for when we get the experiment key from gql and need our ExperimentFlag name
const keyToExperimentFlag: Record<string, ExperimentFlag> = Object.entries(
  experimentFlagMap,
).reduce((all, [flag, key]) => {
  all[key] = flag;
  return all;
}, {});

const getExperimentFromLocalStorage = (key: string): string | undefined => {
  const flag = window.localStorage.getItem(`${key}.reception.od`);
  if (flag) {
    // eslint-disable-next-line no-console
    console.info('Using experimentFlag from localStorage:', key, flag);
    return flag;
  }
  return undefined;
};

export const initialExperimentFlagsState: ExperimentFlags = Object.keys(experimentFlagMap).reduce(
  (allFlags: ExperimentFlags, key: ExperimentFlag) => {
    allFlags[key] = allFlags[key] ?? null;
    return allFlags;
  },
  {} as ExperimentFlags,
);

const slice = createSlice({
  name: 'experiments',
  initialState: initialExperimentFlagsState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      api.endpoints.triggerExperimentImpression.matchFulfilled,
      (state, { payload, meta }) => {
        const variations = payload.reception.getSellerInput?.sellerInput?.experimentVariations;
        if (variations && variations.length) {
          variations.forEach(({ name, variation }) => {
            state[keyToExperimentFlag[name]] = variation ?? null;
          });
        }

        const testFlag = getExperimentFromLocalStorage(meta.arg.originalArgs.experimentFlag);
        if (testFlag) {
          // eslint-disable-next-line no-console
          console.info(
            'Setting experiment flag from local storage',
            meta.arg.originalArgs.experimentFlag,
            testFlag,
          );
          state[meta.arg.originalArgs.experimentFlag] = testFlag;
        }
      },
    );

    builder.addMatcher(api.endpoints.getVariantQuery.matchFulfilled, (state, { payload, meta }) => {
      const experimentFlag = meta.arg.originalArgs.experimentFlag;
      const variation = payload.variant.getVariant?.variant?.value;
      if (variation) {
        state[experimentFlag] = variation ?? null;
      }

      const testFlag = getExperimentFromLocalStorage(meta.arg.originalArgs.experimentFlag);
      if (testFlag) {
        // eslint-disable-next-line no-console
        console.info(
          'Setting experiment flag from local storage',
          meta.arg.originalArgs.experimentFlag,
          testFlag,
        );
        state[meta.arg.originalArgs.experimentFlag] = testFlag;
      }
    });
  },
});

export default slice.reducer;
